import React, { useMemo } from "react";
import { Spinner, Toast } from "react-bootstrap";
import SubjectImage from "../../Components/SubjectImage/SubjectImage";

const MAX_WIDTH = 20;

function truncateLocation(tier1, tier2, tier3) {
  if (tier1 && tier2 && tier3) {
    const full = `${tier1} - ${tier2} - ${tier3}`;
    if (full.length <= MAX_WIDTH) return full;
    const two = `${tier2} - ${tier3}`;
    if (two.length <= MAX_WIDTH) return two;
    if (tier3.length <= MAX_WIDTH) return tier3;
  } else if (tier1 && tier2) {
    const two = `${tier1} - ${tier2}`;
    if (two.length <= MAX_WIDTH) return two;
    if (tier2.length <= MAX_WIDTH) return tier2;
  } else if (tier1 && tier1.length <= MAX_WIDTH) {
    return tier1;
  }
  return "--";
}

const ToastItem = React.memo(function ToastItem({
  toast,
  userData,
  imageSasToken,
  startedResponding,
  respondingButton,
  busyButton,
  removeToast,
  onClose,
  navigate,
}) {
  const computed = useMemo(() => {
    const alertStates = userData?.entityAssets?.alertStates;
    const base64SvgIndex = alertStates?.findIndex(
      (state) => state.priorityValue === toast?.documentInfo?.priorityValue,
    ) ?? -1;

    const fallbackState = alertStates?.[alertStates?.length - 1];
    const base64Svg =
      base64SvgIndex !== -1
        ? alertStates[base64SvgIndex]?.wellnessSvg
        : fallbackState?.lightThemeSvg;

    const colorStyle =
      base64SvgIndex !== -1
        ? { color: alertStates[base64SvgIndex]?.wellnessColor }
        : { color: fallbackState?.lightThemeCardColor };

    const priorityDescription =
      base64SvgIndex !== -1
        ? alertStates[base64SvgIndex]?.wellnessPriorityDescription
        : fallbackState?.priorityDescription;

    const showBelowSubjectName = userData?.entityInformation?.entityPreference
      ?.showJmsId
      ? `ID: ${toast?.documentInfo?.jmsId || "---------"}`
      : userData?.entityInformation?.entityPreference?.showBookingId
        ? `ID: ${toast?.documentInfo?.bookingId || "---------"}`
        : "";

    const doc = toast?.documentInfo;
    const standardLocation = doc
      ? truncateLocation(doc.tier1Location, doc.tier2Location, doc.tier3Location)
      : "--";

    const lorawanLocation = doc?.loRaWANLastLocation
      ? truncateLocation(
          doc.loRaWANLastLocation.tier1,
          doc.loRaWANLastLocation.tier2,
          doc.loRaWANLastLocation.tier3,
        )
      : "--";

    const truncatedLocationType =
      doc?.connectionType === "lorawan" ? lorawanLocation : standardLocation;

    const showJmsOrBookingId = userData?.entityInformation?.entityPreference
      ?.showJmsId
      ? doc?.jmsId
        ? `ID: ${doc.jmsId}`
        : ""
      : userData?.entityInformation?.entityPreference?.showBookingId
        ? doc?.bookingId
          ? `ID: ${doc.bookingId}`
          : ""
        : "";

    const displayName =
      doc?.firstName && doc?.lastName
        ? `${doc.firstName} ${doc.lastName}`
        : doc?.firstName || doc?.lastName || "--";

    const imageUrl = doc?.imageUrl || null;

    const fallbackImageSrc = doc?.userId
      ? "/svgs/Light_Checkout_Sensor_PFP.svg"
      : "/svgs/MonitoredSubjectCard/user-circle.svg";

    return {
      colorStyle,
      priorityDescription,
      showBelowSubjectName,
      truncatedLocationType,
      showJmsOrBookingId,
      displayName,
      imageUrl,
      fallbackImageSrc,
    };
  }, [toast, userData?.entityAssets?.alertStates, userData?.entityInformation?.entityPreference]);

  const {
    colorStyle,
    priorityDescription,
    showBelowSubjectName,
    truncatedLocationType,
    showJmsOrBookingId,
    displayName,
    imageUrl,
    fallbackImageSrc,
  } = computed;

  if (
    toast.category === "Critical" &&
    !userData?.userInfo?.disableAlertPopups
  ) {
    return (
      <Toast
        className="critical-toast-card"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
        autohide={false}
      >
        <div className="critical-toast-card-top-section">
          <SubjectImage
            imageUrl={imageUrl}
            fallbackSrc={fallbackImageSrc}
            className="critical-toast-card-profile-image"
            onError={(e) => {
              if (toast?.documentInfo?.imageUrl) {
                e.target.src = "/svgs/MonitoredSubjectCard/user-circle.svg";
                e.target.className = "critical-toast-card-profile-image";
              }
            }}
          />
        </div>
        <div className="critical-toast-information-section">
          <div className="critical-toast-information-name">{displayName}</div>
          <div className="critical-toast-information-jms-booking-id">
            {showJmsOrBookingId}
          </div>
          <div className="critical-toast-information-description-location-container">
            <div
              className="critical-toast-information-description"
              style={colorStyle}
            >
              {priorityDescription}
            </div>
            <div className="critical-toast-information-location-container">
              <div className="critical-toast-information-location-attribute">
                {`Location: `}
              </div>
              <div className="critical-toast-information-truncated-location">
                {` ${truncatedLocationType}`}
              </div>
            </div>
          </div>
        </div>

        <div className="critical-toast-card-bottom-section">
          {!startedResponding ? (
            <React.Fragment>
              {!userData?.entityInformation?.alertPreferences?.highWellnessModal
                ?.occupiedButton?.hide && (
                <div
                  onClick={() => busyButton(toast.documentInfo, toast.id)}
                  className="critical-toast-card-buttons critical-toast-card-occupied-button"
                >
                  {userData?.entityInformation?.alertPreferences
                    ?.highWellnessModal?.occupiedButton?.text || "Occupied"}
                </div>
              )}

              <div
                onClick={() => respondingButton(toast.documentInfo, toast.id)}
                className="critical-toast-card-buttons critical-toast-card-responding-button"
              >
                {userData?.entityInformation?.alertPreferences
                  ?.highWellnessModal?.respondingButton?.text || "Responding"}
              </div>
            </React.Fragment>
          ) : (
            <div className="d-flex text-center align-items-center justify-content-center w-100">
              <Spinner />
            </div>
          )}
        </div>
      </Toast>
    );
  }

  if (toast.category === "Critical") return null;

  return (
    <Toast
      onClose={() => removeToast(toast.id)}
      autohide
      delay={
        userData?.entityInformation?.alertPreferences?.popupsDelay || 5000
      }
      className="status-change-container"
      style={{ zIndex: 1000, cursor: "pointer" }}
      onClick={(e) => {
        navigate(`/user/dashboard/subject/${toast?.documentInfo?.id}`);
        onClose(e, toast.id);
      }}
    >
      <SubjectImage
        imageUrl={imageUrl}
        fallbackSrc={fallbackImageSrc}
        className="status-change-toast-image"
        onError={(e) => {
          if (toast?.documentInfo?.imageUrl) {
            e.target.src = "/svgs/MonitoredSubjectCard/user-circle.svg";
            e.target.className = "status-change-toast-image";
          }
        }}
      />
      <div className="toast-subject-card-information-container">
        <div className="toast-subject-card-name"> {displayName}</div>
        <div className="toast-subject-card-id">{showBelowSubjectName}</div>
      </div>
      <div className="d-flex w-100 justify-content-end gap-2">
        <div className="toast-subject-card-status" style={colorStyle}>
          {priorityDescription}
        </div>
        <img
          src="/svgs/Modal/x-close.svg"
          alt="close"
          onClick={(e) => onClose(e, toast.id)}
        />
      </div>
    </Toast>
  );
});

export default ToastItem;
